/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/display-name */
import { Box } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import { DATA_FILE_NAME, DATA_FILE_PATH, PILLAR_INFO, PillarType } from 'constant'
import { useActions, useAppSelector } from 'hooks'
import React, { useCallback, useEffect } from 'react'
import { inputFilter } from 'store/AIMetricOT/reducerSlice'
import { useDataWithMetric } from 'utils'
import { getCheckedHaveData } from 'utils/getCheckedNoData'
import {
  getMetricCodeByName,
  getMetricList,
  getMetricNameByCode,
  getMetricNameByShortNameRaw,
} from 'utils/getMetricListByType'

import MetricSearchDropdown from 'components/common/SearchDropdown/MetricSearchDropdown'
import { DataKey, MetricType } from 'types'

export default function MetricDropdown() {
  const { setMetric, setMetricOTType } = useActions()
  const currentFilter = useAppSelector(inputFilter)
  const {
    country: currentCountry,
    countryType: currentCountryType,
    metric: currentMetric,
    metricType: currentMetricType, // eco rad inf ...
    metricValue: currentMetricValue, // perCapita absolute
    chartType: currentChartType, // world bar
  } = currentFilter

  const isGlobal = currentCountryType === 'global'
  const initialMetric = currentMetric[currentMetricType][currentMetricValue]
  const [selectedMetric, setSelectedMetric] = React.useState<string | undefined>(initialMetric)
  const csvData = useDataWithMetric(`${DATA_FILE_PATH}${DATA_FILE_NAME}.csv`)
  const metricGroupData = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)

  const getMultipleData = useCallback(
    (dataKey: DataKey) => {
      const metricListByType = getMetricList(metricGroupData, dataKey, currentMetricValue)
      const metricCheckedList =
        getCheckedHaveData(metricListByType, csvData, isGlobal ? 'Global' : currentCountry, currentChartType) || []
      const closestHaveData = metricCheckedList?.find((item) => {
        return Object.values(item)[1] === false
      })
      const selectedMetricNoData = metricCheckedList?.find((item) => Object.keys(item)[0] === selectedMetric)?.isNoData
      const closestEnabledMetric =
        selectedMetricNoData === false ? selectedMetric : closestHaveData && Object.keys(closestHaveData)[0]

      return {
        metricCheckedList,
        closestHaveData,
        selectedMetricNoData,
        closestEnabledMetric,
      }
    },
    [metricGroupData, currentMetricValue, csvData, isGlobal, currentCountry],
  )

  const metricListInfo = (() => {
    const dataKey = PILLAR_INFO?.find((p: PillarType) => p.metricType === currentMetricType)?.dataKey
    if (!dataKey) {
      return { metricList: [], isSelectedMetricNoData: false, closestEnableMetric: '' }
    }
    const metricListByType = getMetricList(metricGroupData, dataKey, currentMetricValue)
    const metricCheckedList = getCheckedHaveData(
      metricListByType,
      csvData,
      isGlobal ? 'Global' : currentCountry,
      currentChartType,
    )
    const closestHaveData = metricCheckedList?.find((item) => Object.values(item)[1] === false)

    const selectedMetricNoData = metricCheckedList?.find((item) => Object.keys(item)[0] === selectedMetric)?.isNoData
    const closestEnabledMetric =
      selectedMetricNoData === false ? selectedMetric : closestHaveData && Object.keys(closestHaveData)[0]

    return {
      metricList: metricCheckedList || [],
      isSelectedMetricNoData: selectedMetricNoData || false,
      closestEnableMetric: closestEnabledMetric || '',
    }
  })()

  const [selectedShowMetric, setSelectedShowMetric] = React.useState<string | undefined>(
    getMetricNameByCode(metricListInfo?.metricList, initialMetric)?.replace(' PC', ''),
  )

  useEffect(() => {
    if (metricListInfo.isSelectedMetricNoData === true && metricListInfo.closestEnableMetric) {
      const closestMetricCodeHasData = getMetricNameByCode(
        metricListInfo.metricList,
        metricListInfo.closestEnableMetric,
      )
      handleChangeDropdown(closestMetricCodeHasData, currentMetricType)
    }
  }, [currentChartType, currentMetricType])

  useEffect(() => {
    setSelectedShowMetric(getMetricNameByCode(metricListInfo?.metricList, initialMetric)?.replace(' PC', ''))
  }, [initialMetric, selectedShowMetric, currentChartType])

  useEffect(() => {
    setSelectedMetric(currentMetric[currentMetricType][currentMetricValue])
  }, [currentMetricValue, currentMetric, currentMetricType, currentChartType])

  useEffect(() => {
    handleSetMetricType(currentMetricType)
  }, [currentCountry])

  const handleChangeDropdown = (value: any, mtType: MetricType) => {
    const newSelectedMetric = () => {
      const dataKey = PILLAR_INFO?.find((p: PillarType) => p.metricType === mtType)!.dataKey
      return getMetricCodeByName(getMultipleData(dataKey).metricCheckedList, value)
    }
    const newSelectedMetricCode = newSelectedMetric()
    if (newSelectedMetricCode) {
      const updateMetric = {
        ...currentMetric,
        [mtType]: { ...currentMetric[mtType], [currentMetricValue]: newSelectedMetricCode },
      }
      setMetric({ metric: updateMetric })
      setSelectedMetric(newSelectedMetricCode)
      setSelectedShowMetric(value)
    }
  }

  const handleSetMetricType = (type: MetricType) => {
    setMetricOTType({ metricType: type })
    const dataKey = PILLAR_INFO?.find((p: PillarType) => p.metricType === type)!.dataKey
    const getData = getMultipleData(dataKey)
    const updateMetric = {
      ...currentMetric,
      [type]: { ...currentMetric[type], [currentMetricValue]: getData.closestEnabledMetric },
    }
    setMetric({ metric: updateMetric })
  }

  return (
    <div>
      {PILLAR_INFO.map((pillar) => {
        let data = getMultipleData(pillar.dataKey).metricCheckedList
        if (currentChartType === 'bar') {
          data = data?.filter(
            (ele: any) =>
              ele?.metricKey !== 'national_ai_strategy_is_released' &&
              ele?.metricKey !== 'pc_national_ai_strategy_is_released',
          )
        }
        return (
          <FormControl
            key={pillar.dataKey}
            sx={{ mx: 0, my: 2, width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '12px' }}>
              <MetricSearchDropdown
                text={pillar.displayName?.toUpperCase()}
                selectedShowMetric={selectedShowMetric}
                dropdownMetricType={pillar.metricType}
                id={`metric-${pillar.metricType}-dropdown`}
                disabled={!data?.length}
                size={'small'}
                handleChangeDropdown={handleChangeDropdown}
                handleSetMetricType={handleSetMetricType}
                data={data}
                height={32}
                fontSize={15}
              />
            </Box>
          </FormControl>
        )
      })}
    </div>
  )
}
